@file:OptIn(ExperimentalTime::class)

package ai.wagemix.net.models

import com.fsryan.foundation.models.toEnumValue
import com.ionspin.kotlin.bignum.integer.toBigInteger
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName
import kotlin.math.max
import kotlin.math.min
import kotlin.time.Clock
import kotlin.math.roundToInt
import kotlin.time.ExperimentalTime

interface WMStatsCollection {

    val area: String
    val endYear: Int?
    val industry: String
    val startYear: Int?
    val totalWeight: Int
    val yearCount: Int
    val regressionMetadata: RegressionMetadata?

    fun orderedStats(): List<Pair<Int, OccupationStats>>

    /**
     * Metadata about regression-derived projected data
     */
    interface RegressionMetadata {
        val regressionType: WMRegressionType
        val projectedYears: Set<Int>
        val lastActualYear: Int

        companion object
    }

    interface OccupationStats {
        val weight: Int
        val title: String
        val years: List<Int>

        fun statForYear(year: Int): OccupationStat?

        companion object
    }

    interface OccupationStat {
        val annualP10: Int?
        val annualP25: Int?
        val annualMedian: Int?
        val annualP75: Int?
        val annualP90: Int?
        val annualMean: Int?
        val cappedFlags: Int
        val hourlyP10: Float?
        val hourlyP25: Float?
        val hourlyMedian: Float?
        val hourlyP75: Float?
        val hourlyP90: Float?
        val hourlyMean: Float?
        val missingFlags: Int

        fun withMultiplier(multiplier: Float): OccupationStat

        companion object
    }

    companion object
}

fun WMStatsCollection.OccupationStats.weightedStatForYear(year: Int, totalWeight: Int): WMStatsCollection.OccupationStat? {
    if (totalWeight <= 0) {
        return null
    }
    return statForYear(year)?.withMultiplier(weight.toFloat() / totalWeight)
}

fun WMStatsCollection.Companion.kxs(
    area: String,
    industry: String,
    occupationStats: Map<Int, WMStatsCollection.OccupationStats>,
    regressionMetadata: WMStatsCollection.RegressionMetadata? = null
): KxsWMStatsCollection {
    return KxsWMStatsCollection(
        area = area,
        industry = industry,
        occupationStats = occupationStats
            .mapKeys { (k, _) -> k.toString() }
            .mapValues { (_, v) -> v.toKxs() },
        regressionMetadata = regressionMetadata?.toKxs()
    )
}

fun WMStatsCollection.toKxs(): KxsWMStatsCollection = when (this) {
    is KxsWMStatsCollection -> this
    else -> WMStatsCollection.kxs(
        area = area,
        industry = industry,
        occupationStats = orderedStats().toMap(),
        regressionMetadata = regressionMetadata
    )
}

fun WMStatsCollection.RegressionMetadata.toKxs(): KxsWMRegressionMetadata = when (this) {
    is KxsWMRegressionMetadata -> this
    else -> WMStatsCollection.RegressionMetadata.kxs(
        regressionType = regressionType,
        projectedYears = projectedYears,
        lastActualYear = lastActualYear
    )
}

// Regression Metadata implementation (must be defined before KxsWMStatsCollection for serialization)
@Serializable
@ConsistentCopyVisibility
@SerialName("WMStatsCollection.RegressionMetadata")
data class KxsWMRegressionMetadata internal constructor(
    @SerialName("regression_type") private val _regressionType: String,
    @SerialName("projected_years") override val projectedYears: Set<Int>,
    @SerialName("last_actual_year") override val lastActualYear: Int
) : WMStatsCollection.RegressionMetadata {
    override val regressionType: WMRegressionType
        get() = _regressionType.toEnumValue(WMRegressionType.Linear)
}

@Serializable
@ConsistentCopyVisibility
@SerialName("WMStatsCollection")
data class KxsWMStatsCollection internal constructor(
    @SerialName("area") override val area: String,
    @SerialName("industry") override val industry: String,
    @SerialName("occupation_stats") private val occupationStats: Map<String, OccupationStats>,
    @SerialName("regression_metadata") override val regressionMetadata: KxsWMRegressionMetadata? = null
) : WMStatsCollection {
    override val endYear: Int?
        get() = occupationStats.values.flatMap { it.years }.maxOrNull()
    override val startYear: Int?
        get() = occupationStats.values.flatMap { it.years }.minOrNull()
    override val totalWeight: Int
        get() = occupationStats.values.sumOf { it.weight }
    override val yearCount: Int
        get() = occupationStats.values.flatMap { it.years }.distinct().size

    override fun orderedStats(): List<Pair<Int, OccupationStats>> {
        return occupationStats
            .mapKeys { (k, _) -> k.toIntOrNull() }
            .entries
            .mapNotNull { (k, v) -> k?.let { it to v } }
            .sortedBy { (k, _) -> k }
    }

    @Serializable
    @ConsistentCopyVisibility
    @SerialName("WMStatsCollection.OccupationStats")
    data class OccupationStats internal constructor(
        @SerialName("weight") override val weight: Int,
        @SerialName("title") override val title: String,
        @SerialName("stats") private val stats: Map<String, OccupationStat>
    ) : WMStatsCollection.OccupationStats {
        override val years: List<Int>
            get() = stats.keys.mapNotNull { it.toIntOrNull() }.sorted()
        override fun statForYear(year: Int): WMStatsCollection.OccupationStat? = stats[year.toString()]
    }

    @Serializable
    @ConsistentCopyVisibility
    @SerialName("WMStatsCollection.OccupationStat")
    data class OccupationStat internal constructor(
        @SerialName("annual_p10") override val annualP10: Int? = null,
        @SerialName("annual_p25") override val annualP25: Int? = null,
        @SerialName("annual_median") override val annualMedian: Int? = null,
        @SerialName("annual_p75") override val annualP75: Int? = null,
        @SerialName("annual_p90") override val annualP90: Int? = null,
        @SerialName("annual_mean") override val annualMean: Int? = null,
        @SerialName("capped_flags") override val cappedFlags: Int = 0,
        @SerialName("hourly_p10") override val hourlyP10: Float? = null,
        @SerialName("hourly_p25") override val hourlyP25: Float? = null,
        @SerialName("hourly_median") override val hourlyMedian: Float? = null,
        @SerialName("hourly_p75") override val hourlyP75: Float? = null,
        @SerialName("hourly_p90") override val hourlyP90: Float? = null,
        @SerialName("hourly_mean") override val hourlyMean: Float? = null,
        @SerialName("missing_flags") override val missingFlags: Int = 0
    ) : WMStatsCollection.OccupationStat {
        override fun withMultiplier(multiplier: Float): OccupationStat {
            return OccupationStat(
                annualP10 = (annualP10?.toDouble()?.times(multiplier))?.toInt(),
                annualP25 = (annualP25?.toDouble()?.times(multiplier))?.toInt(),
                annualMedian = (annualMedian?.toDouble()?.times(multiplier))?.toInt(),
                annualP75 = (annualP75?.toDouble()?.times(multiplier))?.toInt(),
                annualP90 = (annualP90?.toDouble()?.times(multiplier))?.toInt(),
                annualMean = (annualMean?.toDouble()?.times(multiplier))?.toInt(),
                cappedFlags = cappedFlags,
                hourlyP10 = hourlyP10?.times(multiplier),
                hourlyP25 = hourlyP25?.times(multiplier),
                hourlyMedian = hourlyMedian?.times(multiplier),
                hourlyP75 = hourlyP75?.times(multiplier),
                hourlyP90 = hourlyP90?.times(multiplier),
                hourlyMean = hourlyMean?.times(multiplier),
                missingFlags = missingFlags
            )
        }
    }
}

fun WMStatsCollection.OccupationStats.Companion.kxs(
    stats: Map<Int, WMStatsCollection.OccupationStat>,
    title: String,
    weight: Int
): KxsWMStatsCollection.OccupationStats {
    return KxsWMStatsCollection.OccupationStats(
        stats = stats.mapKeys { (k, _) -> k.toString() }
            .mapValues { (_, v) -> v.toKxs() },
        title = title,
        weight = weight
    )
}

fun WMStatsCollection.OccupationStats.toKxs(): KxsWMStatsCollection.OccupationStats = when (this) {
    is KxsWMStatsCollection.OccupationStats -> this
    else -> WMStatsCollection.OccupationStats.kxs(
        weight = weight,
        title = title,
        stats = years.mapNotNull { year ->
            statForYear(year)?.let { year to it }
        }.toMap()
    )
}

fun WMStatsCollection.OccupationStat.Companion.kxs(
    annualP10: Int?,
    annualP25: Int?,
    annualMedian: Int?,
    annualP75: Int?,
    annualP90: Int?,
    annualMean: Int?,
    cappedFlags: Int,
    hourlyP10: Float?,
    hourlyP25: Float?,
    hourlyMedian: Float?,
    hourlyP75: Float?,
    hourlyP90: Float?,
    hourlyMean: Float?,
    missingFlags: Int
): KxsWMStatsCollection.OccupationStat {
    return KxsWMStatsCollection.OccupationStat(
        annualP10 = annualP10,
        annualP25 = annualP25,
        annualMedian = annualMedian,
        annualP75 = annualP75,
        annualP90 = annualP90,
        annualMean = annualMean,
        cappedFlags = cappedFlags,
        hourlyP10 = hourlyP10,
        hourlyP25 = hourlyP25,
        hourlyMedian = hourlyMedian,
        hourlyP75 = hourlyP75,
        hourlyP90 = hourlyP90,
        hourlyMean = hourlyMean,
        missingFlags = missingFlags
    )
}

fun WMStatsCollection.OccupationStat.toKxs(): KxsWMStatsCollection.OccupationStat = when (this) {
    is KxsWMStatsCollection.OccupationStat -> this
    else -> WMStatsCollection.OccupationStat.kxs(
        annualP10 = annualP10,
        annualP25 = annualP25,
        annualMedian = annualMedian,
        annualP75 = annualP75,
        annualP90 = annualP90,
        annualMean = annualMean,
        cappedFlags = cappedFlags,
        hourlyP10 = hourlyP10,
        hourlyP25 = hourlyP25,
        hourlyMedian = hourlyMedian,
        hourlyP75 = hourlyP75,
        hourlyP90 = hourlyP90,
        hourlyMean = hourlyMean,
        missingFlags = missingFlags
    )
}

fun WMStatsCollection.OccupationStat.isAWageCapFlagHit(flags: Int): Boolean {
    return when (flags) {
        0 -> false
        else -> (cappedFlags and flags) != 0
    }
}
fun WMStatsCollection.OccupationStat.isAMissingWageFlagHit(flags: Int): Boolean {
    return when (flags) {
        0 -> false
        else -> (missingFlags and flags) != 0
    }
}

/**
 * Creates a new [WMStatsCollection] with the same area and industry, but with
 * updated weights for each occupation. If a weight for a specific occupation is
 * not provided in the [newWeights] map, the original stats for that occupation
 * will be retained.
 */
fun WMStatsCollection.withNewWeights(newWeights: Map<Int, Int>): WMStatsCollection {
    val newOccupationStats = orderedStats().associate { (collectionId, stats) ->
        when (val newWeight = newWeights[collectionId]) {
            null -> collectionId to stats // No new weight for this year, keep the original stats
            else -> collectionId to stats.toKxs().copy(weight = newWeight)
        }
    }
    return WMStatsCollection.kxs(area = area, industry = industry, occupationStats = newOccupationStats)
}

fun WMStatsCollection.toAnnualIncomeChart(): WMIntegerChartData {
    val allSeries = toAnnualIncomeSeries()
    val orderedStats = orderedStats()
    val title = orderedStats.joinToString { (_, stats) ->
        val floatValue = stats.weight.toFloat() / totalWeight
        val cutOffDecimals = (floatValue * 1000).toInt()
        val percentage = cutOffDecimals.toFloat() / 10
        "${stats.title} ($percentage%)"
    }
    return WMIntegerChartData.kxs(
        name = "Annual: $title",
        // this kind of sucks. We should instead have the year as the top-level
        // key and the occupation stats as the next-level key
        labels = orderedStats.flatMap { (_, stats) -> stats.years }.distinct().sorted().map { it.toString() },
        datasets = allSeries
    )
}

fun WMStatsCollection.toAnnualIncomeSeries(): List<WMIntegerDataSeriesData> {
    // First, find the common years across all occupation stats
    val allStats = orderedStats()
    val commonYears = if (allStats.isEmpty()) {
        emptySet()
    } else {
        allStats.map { (_, stats) -> stats.years.toSet() }
            .reduce { acc, years -> acc.intersect(years) }
    }

    val p10Data = mutableMapOf<Int, Float>()
    val p25Data = mutableMapOf<Int, Float>()
    val medianData = mutableMapOf<Int, Float>()
    val p75Data = mutableMapOf<Int, Float>()
    val p90Data = mutableMapOf<Int, Float>()
    
    allStats.forEach { (_, stats) ->
        val multiplier = stats.weight.toFloat() / totalWeight
        // Only process years that are common to all occupations
        commonYears.forEach { year ->
            stats.statForYear(year)?.withMultiplier(multiplier)?.let { stat ->
                val current = p10Data.getOrPut(year) { 0F }
                p10Data[year] = current + (stat.annualP10?.toFloat() ?: 0F)
                val currentP25 = p25Data.getOrPut(year) { 0F }
                p25Data[year] = currentP25 + (stat.annualP25?.toFloat() ?: 0F)
                val currentMedian = medianData.getOrPut(year) { 0F }
                medianData[year] = currentMedian + (stat.annualMedian?.toFloat() ?: 0F)
                val currentP75 = p75Data.getOrPut(year) { 0F }
                p75Data[year] = currentP75 + (stat.annualP75?.toFloat() ?: 0F)
                val currentP90 = p90Data.getOrPut(year) { 0F }
                p90Data[year] = currentP90 + (stat.annualP90?.toFloat() ?: 0F)
            }
        }
    }

    val p10 = p10Data.toIntegerSeriesData(displayName = "P10", id = "annual_p10")
    val p25 = p25Data.toIntegerSeriesData(displayName = "P25", id = "annual_p25")
    val median = medianData.toIntegerSeriesData(displayName = "Median", id = "annual_median")
    val p75 = p75Data.toIntegerSeriesData(displayName = "P75", id = "annual_p75")
    val p90 = p90Data.toIntegerSeriesData(displayName = "P90", id = "annual_p90")
    return listOf(p10, p25, median, p75, p90)
}

fun Map<Int, Float>.toIntegerSeriesData(displayName: String, id: String): WMIntegerDataSeriesData {
    return WMIntegerDataSeriesData.kxs(
        displayName = displayName,
        id = id,
        points = entries.sortedBy { it.key }.map { (year, value) ->
            WMIntegerDataPointData.kxs(value = value.toInt().toBigInteger())
        }
    )
}

/**
 * Converts a map of year->value to series data, aligned with a specific list of years.
 * Creates points for all years in the labels list, using the map values where available.
 * This ensures datasets align properly when split into actual/projected segments.
 */
fun Map<Int, Float>.toIntegerSeriesDataAligned(displayName: String, id: String, allYears: List<Int>): WMIntegerDataSeriesData {
    return WMIntegerDataSeriesData.kxs(
        displayName = displayName,
        id = id,
        points = allYears.map { year ->
            val value = this[year] ?: 0F
            WMIntegerDataPointData.kxs(value = value.toInt().toBigInteger())
        }
    )
}

fun WMStatsCollection.toHourlyWageChart(): WMIntegerChartData {
    val allSeries = toHourlyWageSeries()
    val orderedStats = orderedStats()
    val title = orderedStats.joinToString { (_, stats) ->
        val floatValue = stats.weight.toFloat() / totalWeight
        val cutOffDecimals = (floatValue * 1000).toInt()
        val percentage = cutOffDecimals.toFloat() / 10
        "${stats.title} ($percentage%)"
    }
    return WMIntegerChartData.kxs(
        name = "Hourly: $title",
        labels = orderedStats.flatMap { (_, stats) -> stats.years }.distinct().sorted().map { it.toString() },
        datasets = allSeries
    )
}

fun WMStatsCollection.toHourlyWageSeries(): List<WMIntegerDataSeriesData> {
    // First, find the common years across all occupation stats
    val allStats = orderedStats()
    val commonYears = if (allStats.isEmpty()) {
        emptySet()
    } else {
        allStats.map { (_, stats) -> stats.years.toSet() }
            .reduce { acc, years -> acc.intersect(years) }
    }

    val p10Data = mutableMapOf<Int, Float>()
    val p25Data = mutableMapOf<Int, Float>()
    val medianData = mutableMapOf<Int, Float>()
    val p75Data = mutableMapOf<Int, Float>()
    val p90Data = mutableMapOf<Int, Float>()
    
    allStats.forEach { (_, stats) ->
        val multiplier = stats.weight.toFloat() / totalWeight
        // Only process years that are common to all occupations
        commonYears.forEach { year ->
            stats.statForYear(year)?.withMultiplier(multiplier)?.let { stat ->
                val current = p10Data.getOrPut(year) { 0F }
                p10Data[year] = current + (stat.hourlyP10 ?: 0F)
                val currentP25 = p25Data.getOrPut(year) { 0F }
                p25Data[year] = currentP25 + (stat.hourlyP25 ?: 0F)
                val currentMedian = medianData.getOrPut(year) { 0F }
                medianData[year] = currentMedian + (stat.hourlyMedian ?: 0F)
                val currentP75 = p75Data.getOrPut(year) { 0F }
                p75Data[year] = currentP75 + (stat.hourlyP75 ?: 0F)
                val currentP90 = p90Data.getOrPut(year) { 0F }
                p90Data[year] = currentP90 + (stat.hourlyP90 ?: 0F)
            }
        }
    }

    val p10 = p10Data.toIntegerSeriesData(displayName = "P10", id = "hourly_p10")
    val p25 = p25Data.toIntegerSeriesData(displayName = "P25", id = "hourly_p25")
    val median = medianData.toIntegerSeriesData(displayName = "Median", id = "hourly_median")
    val p75 = p75Data.toIntegerSeriesData(displayName = "P75", id = "hourly_p75")
    val p90 = p90Data.toIntegerSeriesData(displayName = "P90", id = "hourly_p90")
    return listOf(p10, p25, median, p75, p90)
}

// Helper function for creating RegressionMetadata
fun WMStatsCollection.RegressionMetadata.Companion.kxs(
    regressionType: WMRegressionType,
    projectedYears: Set<Int>,
    lastActualYear: Int
): KxsWMRegressionMetadata {
    return KxsWMRegressionMetadata(
        _regressionType = regressionType.stringValue,
        projectedYears = projectedYears,
        lastActualYear = lastActualYear
    )
}

private fun WMStatsCollection.closestYearStatsFun(hourly: Boolean): (p: Int, year: Int) -> Float? {
    val p10Data = mutableMapOf<Int, Float>()
    val p25Data = mutableMapOf<Int, Float>()
    val medianData = mutableMapOf<Int, Float>()
    val p75Data = mutableMapOf<Int, Float>()
    val p90Data = mutableMapOf<Int, Float>()
    var lowYear = Int.MAX_VALUE
    var highYear = Int.MIN_VALUE
    orderedStats().forEach { (_, stats) ->
        val multiplier = stats.weight.toFloat() / totalWeight
        stats.years.forEach { year ->
            lowYear = min(lowYear, year)
            highYear = max(highYear, year)
            stats.statForYear(year)?.withMultiplier(multiplier)?.let { stat ->
                val current = p10Data.getOrPut(year) { 0F }
                p10Data[year] = current + ((if (hourly) stat.hourlyP10 else stat.annualP10?.toFloat()) ?: 0F)
                val currentP25 = p25Data.getOrPut(year) { 0F }
                p25Data[year] = currentP25 + ((if (hourly) stat.hourlyP25 else stat.annualP25?.toFloat()) ?: 0F)
                val currentMedian = medianData.getOrPut(year) { 0F }
                medianData[year] = currentMedian + ((if (hourly) stat.hourlyMedian else stat.annualMedian?.toFloat()) ?: 0F)
                val currentP75 = p75Data.getOrPut(year) { 0F }
                p75Data[year] = currentP75 + ((if (hourly) stat.hourlyP75 else stat.annualP75?.toFloat()) ?: 0F)
                val currentP90 = p90Data.getOrPut(year) { 0F }
                p90Data[year] = currentP90 + ((if (hourly) stat.hourlyP90 else stat.annualP90?.toFloat()) ?: 0F)
            }
        }
    }
    return { p, year ->
        when {
            year < lowYear -> null
            else -> {
                // Years below lowYear return null; years above highYear are clamped to highYear
                val clampedYear = min(highYear, year)
                when (p) {
                    10 -> p10Data[clampedYear]
                    25 -> p25Data[clampedYear]
                    50 -> medianData[clampedYear]
                    75 -> p75Data[clampedYear]
                    90 -> p90Data[clampedYear]
                    else -> null
                }
            }
        }
    }
}


fun WMStatsCollection.evaluateBadgeLevel(
    hourly: Boolean,
    low: Float,
    high: Float,
    year: Int = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
): WMBadge {
    val mid = (low + high) / 2F

    // Use requested year, clamped to endYear when available
    val targetYear = this.endYear?.let { min(year, it) } ?: year
    
    val findClosestYearStats = closestYearStatsFun(hourly)
    return findClosestYearStats(90, targetYear)?.let { p90 ->
        if (p90 > 0F && p90 < mid) WMBadge.Diamond else null
    } ?: findClosestYearStats(75, targetYear)?.let { p75 ->
        if (p75 > 0F && p75 < mid) WMBadge.Platinum else null
    } ?: findClosestYearStats(50, targetYear)?.let { median ->
        if (median > 0F && median < mid) WMBadge.Gold else null
    } ?: findClosestYearStats(25, targetYear)?.let { p25 ->
        if (p25 > 0F && p25 < mid) WMBadge.Silver else null
    } ?: findClosestYearStats(10, targetYear)?.let { p10 ->
        if (p10 > 0F && p10 < mid) WMBadge.Bronze else null
    } ?: WMBadge.None
}

fun WMStatsCollection.evaluateBadgeLevel(
    hourly: Boolean,
    payRange: WMSharedMixCreate.PayRange,
    year: Int = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
): WMBadge = evaluateBadgeLevel(
    hourly = hourly,
    low = payRange.low,
    high = payRange.high,
    year = year
)

/**
 * The badge a pay range earns for a shared mix: against this collection projected forward to [currentYear] when its
 * data ends earlier, as the shared page's headline figures are. The share dialog's preview and the API's shared mix
 * both rate through this, so the badge a user is shown is the badge the link carries.
 */
fun WMStatsCollection.evaluateSharedBadgeLevel(hourly: Boolean, low: Float, high: Float, currentYear: Int): WMBadge {
    val endYear = endYear ?: return evaluateBadgeLevel(hourly = hourly, low = low, high = high, year = currentYear)
    val projected = if (currentYear > endYear) addYears(currentYear - endYear) else this
    return projected.evaluateBadgeLevel(hourly = hourly, low = low, high = high, year = currentYear)
}

fun WMStatsCollection.addYears(
    count: Int,
    regressionType: WMRegressionType = WMRegressionType.Linear
): WMStatsCollection {
    if (count <= 0) {
        return this
    }
    if (count > 10) {
        throw IllegalArgumentException("Cannot project more than 10 years")
    }

    // Determine the last actual year from existing data
    val existingMetadata = this.regressionMetadata
    val lastActualYear = existingMetadata?.lastActualYear ?: (endYear ?: return this)
    val existingProjectedYears = existingMetadata?.projectedYears ?: emptySet()

    // Build new occupation stats with projected data
    val newOccupationStats = orderedStats().associate { (occupationId, occupationStats) ->
        val historicalYears = occupationStats.years

        if (historicalYears.isEmpty()) {
            return@associate occupationId to occupationStats
        }

        // Extract historical data points for each metric
        val annualP10Points = mutableListOf<Pair<Int, Double>>()
        val annualP25Points = mutableListOf<Pair<Int, Double>>()
        val annualMedianPoints = mutableListOf<Pair<Int, Double>>()
        val annualP75Points = mutableListOf<Pair<Int, Double>>()
        val annualP90Points = mutableListOf<Pair<Int, Double>>()
        val annualMeanPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP10Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP25Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMedianPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP75Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP90Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMeanPoints = mutableListOf<Pair<Int, Double>>()

        historicalYears.forEach { year ->
            // Only use actual historical data, not existing projections
            if (year <= lastActualYear) {
                occupationStats.statForYear(year)?.let { stat ->
                    stat.annualP10?.let { annualP10Points.add(year to it.toDouble()) }
                    stat.annualP25?.let { annualP25Points.add(year to it.toDouble()) }
                    stat.annualMedian?.let { annualMedianPoints.add(year to it.toDouble()) }
                    stat.annualP75?.let { annualP75Points.add(year to it.toDouble()) }
                    stat.annualP90?.let { annualP90Points.add(year to it.toDouble()) }
                    stat.annualMean?.let { annualMeanPoints.add(year to it.toDouble()) }
                    stat.hourlyP10?.let { hourlyP10Points.add(year to it.toDouble()) }
                    stat.hourlyP25?.let { hourlyP25Points.add(year to it.toDouble()) }
                    stat.hourlyMedian?.let { hourlyMedianPoints.add(year to it.toDouble()) }
                    stat.hourlyP75?.let { hourlyP75Points.add(year to it.toDouble()) }
                    stat.hourlyP90?.let { hourlyP90Points.add(year to it.toDouble()) }
                    stat.hourlyMean?.let { hourlyMeanPoints.add(year to it.toDouble()) }
                }
            }
        }

        // Calculate regression for each metric
        val annualP10Regression = calculateLinearRegressionFormula(annualP10Points)
        val annualP25Regression = calculateLinearRegressionFormula(annualP25Points)
        val annualMedianRegression = calculateLinearRegressionFormula(annualMedianPoints)
        val annualP75Regression = calculateLinearRegressionFormula(annualP75Points)
        val annualP90Regression = calculateLinearRegressionFormula(annualP90Points)
        val annualMeanRegression = calculateLinearRegressionFormula(annualMeanPoints)
        val hourlyP10Regression = calculateLinearRegressionFormula(hourlyP10Points)
        val hourlyP25Regression = calculateLinearRegressionFormula(hourlyP25Points)
        val hourlyMedianRegression = calculateLinearRegressionFormula(hourlyMedianPoints)
        val hourlyP75Regression = calculateLinearRegressionFormula(hourlyP75Points)
        val hourlyP90Regression = calculateLinearRegressionFormula(hourlyP90Points)
        val hourlyMeanRegression = calculateLinearRegressionFormula(hourlyMeanPoints)

        // Project forward count years
        val projectedStats = (1..count).associate { offset ->
            val projectedYear = lastActualYear + offset
            val projectedStat = WMStatsCollection.OccupationStat.kxs(
                annualP10 = annualP10Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP25 = annualP25Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualMedian = annualMedianRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP75 = annualP75Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP90 = annualP90Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualMean = annualMeanRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                cappedFlags = 0, // Projected data has no capped flags
                hourlyP10 = hourlyP10Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP25 = hourlyP25Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyMedian = hourlyMedianRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP75 = hourlyP75Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP90 = hourlyP90Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyMean = hourlyMeanRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                missingFlags = 0 // Projected data has no missing flags
            )
            projectedYear to projectedStat
        }

        // Combine historical + projected stats
        val combinedStats = historicalYears.associateWith { year ->
            occupationStats.statForYear(year)!!
        } + projectedStats

        val newOccupationStats = WMStatsCollection.OccupationStats.kxs(
            stats = combinedStats,
            title = occupationStats.title,
            weight = occupationStats.weight
        )

        occupationId to newOccupationStats
    }

    // Build new regression metadata
    val newProjectedYears = existingProjectedYears + (lastActualYear + 1..lastActualYear + count).toSet()
    val newMetadata = WMStatsCollection.RegressionMetadata.kxs(
        regressionType = regressionType,
        projectedYears = newProjectedYears,
        lastActualYear = lastActualYear
    )

    // Return new stats collection with projected data
    return WMStatsCollection.kxs(
        area = area,
        industry = industry,
        occupationStats = newOccupationStats,
        regressionMetadata = newMetadata
    )
}

/**
 * Result of linear regression calculation
 * Formula: y = slope * x + intercept
 */
data class LinearRegressionResult(val slope: Double, val intercept: Double) {
    /**
     * Predicts the y value for a given x value using the linear regression equation
     * @param x The input value (typically a year like 2026, 2027, etc.)
     * @return The predicted y value
     */
    fun predict(x: Int): Double = slope * x + intercept
}

/**
 * Calculate linear regression using least squares method.
 * This function is isolated so the formula can be easily modified or replaced.
 *
 * Formula:
 * y = mx + b where:
 * m (slope) = (n*Σxy - Σx*Σy) / (n*Σx² - (Σx)²)
 * b (intercept) = (Σy - m*Σx) / n
 *
 * One point has no trend to fit, so the result is a flat line through it (slope 0).
 *
 * @param points List of (x, y) pairs where x is typically a year (2020, 2021, etc.) and y is the wage value
 * @return LinearRegressionResult with slope and intercept, or null when there are no points or every point
 * shares one x with differing y values
 */
fun calculateLinearRegressionFormula(points: List<Pair<Int, Double>>): LinearRegressionResult? {
    if (points.isEmpty()) {
        return null
    }
    if (points.size == 1) {
        return LinearRegressionResult(slope = 0.0, intercept = points.single().second)
    }

    val n = points.size.toDouble()
    val sumX = points.sumOf { it.first.toDouble() }
    val sumY = points.sumOf { it.second }
    val sumXY = points.sumOf { it.first.toDouble() * it.second }
    val sumX2 = points.sumOf { it.first.toDouble() * it.first.toDouble() }

    val denominator = n * sumX2 - sumX * sumX
    if (denominator == 0.0) return null // Prevent division by zero

    val slope = (n * sumXY - sumX * sumY) / denominator
    val intercept = (sumY - slope * sumX) / n

    return LinearRegressionResult(slope, intercept)
}